import moment from 'moment-timezone';

import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type {
  UserWorkJourneyScheduleDetails,
  UserWorkJourneyScheduleSource,
} from '@src/models/interfaces/userWorkJourneySchedule.interface';
import { DateUtil } from '@src/packages/utils/dates';
import { addressFormat } from '@src/util/format/addressFormat';

type TimestampLike = { _seconds?: number } | null | undefined;

export class UserWorkJourneyScheduleMapper {
  private static secondsOrNull(value: TimestampLike): number | null {
    return typeof value?._seconds === 'number' ? value._seconds : null;
  }

  /**
   * Prefer a record whose scheduledStartAt falls on the current calendar day
   * in the app timezone (same day window as Real-Time Elastic `now/d`…`now+1d/d`).
   * When several match, pick the latest scheduled start. Returns null when none
   * are scheduled today (does not fall back to older days).
   */
  static pickAttendanceRecord(
    records: IAttendanceRecord[],
    now: Date = new Date(),
    timeZone: string = DateUtil.getDefaultTimeZone(),
  ): IAttendanceRecord | null {
    if (!records.length) {
      return null;
    }

    const dayStartSeconds = Math.floor(
      moment.tz(now, timeZone).startOf('day').valueOf() / 1000,
    );
    const dayEndSeconds = Math.floor(
      moment.tz(now, timeZone).add(1, 'day').startOf('day').valueOf() / 1000,
    );

    const todayRecords = records
      .filter(record => {
        const start = UserWorkJourneyScheduleMapper.secondsOrNull(record.scheduledStartAt);
        return start !== null && start >= dayStartSeconds && start < dayEndSeconds;
      })
      .sort(
        (a, b) =>
          (UserWorkJourneyScheduleMapper.secondsOrNull(b.scheduledStartAt) ?? 0) -
          (UserWorkJourneyScheduleMapper.secondsOrNull(a.scheduledStartAt) ?? 0),
      );

    return todayRecords[0] ?? null;
  }

  static fromGeneralUserInfo(
    source: UserWorkJourneyScheduleSource,
    now: Date = new Date(),
    timeZone: string = DateUtil.getDefaultTimeZone(),
  ): UserWorkJourneyScheduleDetails | null {
    const { attendanceRecords, workJourneyConfig } = source;
    const record = UserWorkJourneyScheduleMapper.pickAttendanceRecord(
      attendanceRecords ?? [],
      now,
      timeZone,
    );

    if (!record && !workJourneyConfig) {
      return null;
    }

    const locationFromRecord = record?.address ? addressFormat(record.address).trim() : '';
    const locationFromConfig = workJourneyConfig?.address
      ? addressFormat(workJourneyConfig.address).trim()
      : '';

    const shiftFromConfig = workJourneyConfig?.workShift as
      | { id?: string; name?: string }
      | null
      | undefined;

    return {
      attendanceRecordId: record?.id?.trim() || null,
      scheduledStartAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(
        record?.scheduledStartAt,
      ),
      scheduledEndAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(record?.scheduledEndAt),
      scheduledBreakStartAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(
        record?.scheduledBreakStartAt,
      ),
      scheduledBreakEndAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(
        record?.scheduledBreakEndAt,
      ),
      locationLabel: locationFromRecord || locationFromConfig || null,
      actualStartAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(record?.actualStartAt),
      actualBreakStartAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(
        record?.actualBreakStartAt,
      ),
      actualBreakEndAtSeconds: UserWorkJourneyScheduleMapper.secondsOrNull(
        record?.actualBreakEndAt,
      ),
      workJourneyConfigId: record?.workJourneyConfigId || workJourneyConfig?.id || null,
      journeyName: workJourneyConfig?.name?.trim() || null,
      workShiftId: record?.workShiftId || shiftFromConfig?.id || null,
      shiftName: shiftFromConfig?.name?.trim() || null,
    };
  }
}
